import { repo } from '../../data/repo';
import { DAY_MS, startOfLocalDay } from '../../domain/pregnancy';
import { firstCompletion } from '../../domain/countToTen';
import { dailyStats, hourHistogram, ZONES, zoneCounts } from '../../domain/stats';
import { hasPremium } from '../../domain/types';
import { fmtDate, fmtTime, t } from '../../i18n';
import { $, $$, esc } from '../dom';
import { register, type Screen } from '../router';

type Tab = 'today' | 'week' | 'zones' | 'hours';
let tab: Tab = 'today';

const lock = (premium: boolean) => premium ? '' :
  `<div class="lock-veil"><div class="lk">🔒</div><b>${esc(t('stats', 'locked'))}</b><button class="btn btn-primary" style="min-height:42px" data-action="go" data-to="paywall">${esc(t('stats', 'unlock'))}</button></div>`;

function body(): string {
  const kicks = repo.kicks;
  const now = Date.now();
  const today = startOfLocalDay(now);
  const premium = hasPremium(repo.entitlement);
  if (!kicks.length) {
    return `<div class="card empty-state"><div class="e">👣</div><b>${esc(t('stats', 'emptyTitle'))}</b><p class="small">${esc(t('stats', 'emptyLead'))}</p></div>`;
  }

  if (tab === 'today') {
    const times = kicks.map(k => k.occurredAt);
    const tk = kicks.filter(k => k.occurredAt >= today).length;
    const yk = kicks.filter(k => k.occurredAt >= today - DAY_MS && k.occurredAt < today).length;
    const c = firstCompletion(times, today, now + 1);
    const diff = tk - yk;
    const sessions = repo.sessions.filter(s => s.startedAt >= today && s.endedAt && s.kickCount > 0).sort((a, b) => b.startedAt - a.startedAt);
    return `
      <div class="grid-2">
        <div class="card stat-tile"><div class="v num" style="color:var(--rose-dark)">${tk}</div><div class="k">${esc(t('common', 'today'))}</div></div>
        <div class="card stat-tile"><div class="v num" style="color:var(--fg2)">${yk}</div><div class="k">${esc(t('common', 'yesterday'))}</div></div>
      </div>
      <div class="card row" style="justify-content:space-between">
        <span>${esc(t('stats', 'reached'))}</span>
        <b style="color:${c ? '#4D7461' : 'var(--fg3)'}">${esc(c ? `✓ ${t('stats', 'reachedIn', { m: c.minutes })} · ${fmtTime(c.at)}` : t('stats', 'notYet'))}</b>
      </div>
      <p class="small muted" style="text-align:center">${esc(diff > 0 ? t('stats', 'moreThan', { n: diff }) : diff < 0 ? t('stats', 'lessThan', { n: -diff }) : t('stats', 'same'))}</p>
      ${sessions.length ? `<div class="card"><h3 style="margin-bottom:4px">${esc(t('stats', 'sessions'))}</h3>${sessions.map(s => `
        <div class="history-item"><div><span class="flag-dot" style="background:${s.reachedTarget ? 'var(--sage)' : 'var(--border)'}"></span>
        <b class="num">${s.kickCount}</b> ${esc(t('common', 'times'))} · <span class="num">${Math.max(1, Math.round(((s.endedAt ?? now) - s.startedAt) / 60000))}</span> ${esc(t('common', 'minutes'))}
        ${s.note ? `<div class="small muted">${esc(s.note)}</div>` : ''}</div><span class="small muted num">${esc(fmtTime(s.startedAt))}</span></div>`).join('')}</div>` : ''}`;
  }

  if (tab === 'week') {
    const days = dailyStats(kicks, now, 7);
    const max = Math.max(10, ...days.map(d => d.kicks));
    const active = days.filter(d => d.kicks > 0);
    const avg = active.length ? Math.round(active.reduce((a, d) => a + d.kicks, 0) / active.length) : 0;
    const reached = days.filter(d => d.completion).length;
    return `
      <div class="card locked-host ${premium ? '' : 'locked'}">
        <h3>${esc(t('stats', 'daily'))}</h3>
        <div class="bars">${days.map(d => `
          <div class="bar ${d.dayStart === today ? 'today' : ''}">
            <div class="col ${d.kicks ? '' : 'empty'}" style="height:${Math.max(3, (d.kicks / max) * 100)}%;${d.completion ? '' : d.kicks ? 'background:var(--amber)' : ''}">${d.kicks ? `<span class="val num">${d.kicks}</span>` : ''}</div>
            <span class="d">${esc(fmtDate(d.dayStart, { weekday: 'short' }))}</span></div>`).join('')}</div>
        <div class="row small muted" style="justify-content:center;gap:14px;margin-top:10px">
          <span><span class="flag-dot" style="background:var(--rose)"></span>${esc(t('stats', 'reached'))}</span>
          <span><span class="flag-dot" style="background:var(--amber)"></span>${esc(t('stats', 'notYet'))}</span></div>
        ${lock(premium)}
      </div>
      <div class="grid-2" style="${premium ? '' : 'filter:blur(5px);pointer-events:none'}">
        <div class="card stat-tile"><div class="v num" style="color:var(--amber)">${avg}</div><div class="k">${esc(t('stats', 'avg'))}</div></div>
        <div class="card stat-tile"><div class="v num" style="color:var(--sage)">${reached}/7</div><div class="k">${esc(t('stats', 'daysReached'))}</div></div>
      </div>`;
  }

  if (tab === 'zones') {
    const counts = zoneCounts(kicks, today - 6 * DAY_MS);
    const total = ZONES.reduce((a, z) => a + counts[z], 0);
    const maxZ = Math.max(1, ...ZONES.map(z => counts[z]));
    const top = total ? ZONES.slice().sort((a, b) => counts[b] - counts[a])[0]! : null;
    return `
      <div class="card ${premium ? '' : 'locked'}">
        <h3 style="margin-bottom:12px">${esc(t('stats', 'whereTitle'))}</h3>
        <div class="zone-map">${ZONES.map(z => {
          const a = counts[z] / maxZ;
          return `<div class="zone-cell" style="background:rgba(212,132,124,${(0.1 + a * 0.75).toFixed(2)});color:${a > 0.6 ? '#fff' : 'var(--fg2)'}"><b>${counts[z]}</b>${esc(t('zones', z))}</div>`;
        }).join('')}</div>
        <p class="small muted" style="text-align:center;margin-top:12px">${top ? esc(t('stats', 'top', { z: t('zones', top), p: Math.round((counts[top] / total) * 100) })) : esc(t('stats', 'noZone'))}</p>
        ${lock(premium)}
      </div>
      <div class="card tip-card"><span style="font-size:18px">ℹ️</span><p>${esc(t('stats', 'zoneNote'))}</p></div>`;
  }

  // hours
  const hist = hourHistogram(kicks, today - 13 * DAY_MS);
  const maxH = Math.max(1, ...hist);
  const peak = hist.indexOf(Math.max(...hist));
  return `
    <div class="card ${premium ? '' : 'locked'}">
      <h3 style="margin-bottom:12px">${esc(t('stats', 'hoursTitle'))}</h3>
      <div class="hours">${hist.map(v => `<i style="height:${Math.max(2, (v / maxH) * 100)}%;${v ? '' : 'background:var(--border)'}"></i>`).join('')}</div>
      <div class="hours-axis"><span>00</span><span>06</span><span>12</span><span>18</span><span>23</span></div>
      <p class="small muted" style="text-align:center;margin-top:10px">${esc(t('stats', 'peak', { h: `${String(peak).padStart(2, '0')}:00–${String((peak + 1) % 24).padStart(2, '0')}:00` }))}</p>
      ${lock(premium)}
    </div>`;
}

const stats: Screen = {
  id: 'stats', nav: 'stats', root: true,
  render: () => `
    <div class="home-head"><h2>${esc(t('stats', 'title'))}</h2></div>
    <div class="stack">
      <div class="seg" id="stats-seg">
        ${(['today', 'week', 'zones', 'hours'] as Tab[]).map(x =>
          `<button class="${x === tab ? 'on' : ''}" data-action="stats-tab" data-tab="${x}">${esc(t('stats', x === 'today' ? 'tabToday' : x === 'week' ? 'tabWeek' : x === 'zones' ? 'tabZones' : 'tabHours'))}</button>`).join('')}
      </div>
      <div id="stats-body" class="stack">${body()}</div>
    </div>`,
  refresh() {
    const b = $('#stats-body'); if (b) b.innerHTML = body();
    $$('#stats-seg button').forEach(x => x.classList.toggle('on', x.dataset.tab === tab));
  },
  actions: { 'stats-tab': el => { tab = el.dataset.tab as Tab; stats.refresh!(); } }
};

register(stats);
